import React, { useEffect, useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';

const OAuthTest: React.FC = () => {
  const { user, signInWithGoogle, hasGmailAccess } = useAuth();
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get debug information
    const getDebugInfo = async () => {
      try {
        const envResponse = await fetch('/api/debug-env');
        const envData = await envResponse.json();
        
        setDebugInfo({
          env: envData,
          user: user,
          hasGmailAccess,
          urlParams: Object.fromEntries(new URLSearchParams(window.location.search)),
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error fetching debug info:', error);
        setDebugInfo({ error: error.message });
      }
    };

    getDebugInfo();
  }, [user, hasGmailAccess]);

  const handleTestOAuth = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('OAuth test failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestGmailAccess = async () => {
    try {
      const response = await fetch('/api/auth/tokens/' + encodeURIComponent(user?.email || ''));
      if (response.ok) {
        const tokens = await response.json();
        console.log('Gmail tokens:', tokens);
        alert('Gmail tokens retrieved successfully! Check console for details.');
      } else {
        alert('Failed to retrieve Gmail tokens');
      }
    } catch (error) {
      console.error('Error testing Gmail access:', error);
      alert('Error testing Gmail access: ' + error.message);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>OAuth Test Page</CardTitle>
          <CardDescription>
            Test Google OAuth authentication and Gmail access
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Button 
              onClick={handleTestOAuth} 
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Testing OAuth...' : 'Test Google OAuth'}
            </Button>
            
            {user && (
              <Button 
                onClick={handleTestGmailAccess}
                variant="outline"
                className="flex-1"
              >
                Test Gmail Access
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">User Status</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span>Logged in:</span>
                  <Badge variant={user ? "default" : "secondary"}>
                    {user ? "Yes" : "No"}
                  </Badge>
                </div>
                {user && (
                  <>
                    <div>Email: {user.email}</div>
                    <div>Role: {user.role}</div>
                  </>
                )}
                <div className="flex items-center gap-2">
                  <span>Gmail Access:</span>
                  <Badge variant={hasGmailAccess ? "default" : "secondary"}>
                    {hasGmailAccess ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">URL Parameters</h3>
              <pre className="text-xs bg-gray-100 p-2 rounded">
                {JSON.stringify(debugInfo.urlParams || {}, null, 2)}
              </pre>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Environment Debug Info</h3>
            <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-96">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthTest; 